import { ActivityV2 } from 'models/activityV2'
import { EventIcon } from '../EventIcon'
import { formatEventTime } from '../utils'
import { ReactNode } from 'react'

interface BaseEventCardProps {
  event: ActivityV2
  children: ReactNode
}

/**
 * Base layout component for all event cards.
 * Provides consistent structure: icon, content area, and timestamp.
 */
export function BaseEventCard({ event, children }: BaseEventCardProps) {
  const time = formatEventTime(event.created_at)

  return (
    <div className="flex items-start gap-3">
      {/* Icon Container */}
      <div className="flex-shrink-0 bg-pistachio-100 rounded-lg p-2 flex items-center justify-center w-10 h-10 border border-white relative z-20">
        <EventIcon
          contentType={event.content_type}
          actionType={event.action_type}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 bg-white rounded-lg border border-pistachio-100 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">{children}</div>

          {/* Timestamp */}
          <div className="flex-shrink-0">
            <span className="font-inter text-xs leading-[1.67] text-right text-[#6E6E6E]">
              {time}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Action verb mapping for different object types and actions.
 */
const ACTION_VERBS: Record<string, Record<string, string>> = {
  CREATE: {
    note: 'added',
    reminder: 'scheduled',
    'sample request': 'submitted',
    default: 'created',
  },
  UPDATE: {
    default: 'updated',
  },
  DELETE: {
    default: 'deleted',
  },
}

/**
 * Generates action text with consistent styling for object types.
 */
export function getActionText(
  actionType: string,
  objectType: string
): ReactNode {
  const objectSpan = (
    <span className="text-fb-dark-green font-bold">{objectType}</span>
  )

  const verbs = ACTION_VERBS[actionType] || ACTION_VERBS.UPDATE
  const verb = verbs[objectType] || verbs.default || 'modified'

  return (
    <>
      {verb} a {objectSpan}
    </>
  )
}
